import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CloseIcon, EyeIcon, EyeOffIcon, LoginIcon, UserIcon } from './Icons';
import './LoginModal.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function LoginModal({ open, onClose }: Props) {
  const { login } = useAuth();
  const [cnpj, setCnpj] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const showRegistrationGuidance = error.includes('Procure a loja fisica');

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  function maskDocument(value: string) {
    const digits = value.replace(/\D/g, '').slice(0, 14);

    if (digits.length <= 11) {
      return digits
        .replace(/^(\d{3})(\d)/, '$1.$2')
        .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
    }

    return digits
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const cnpjClean = cnpj.replace(/\D/g, '');
      await login(cnpjClean, password);
      setCnpj('');
      setPassword('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content surface-card" onClick={e => e.stopPropagation()}>
        <div className="modal-hero">
          <span className="modal-hero-badge">
            <UserIcon size={16} />
            Acesso seguro
          </span>
          <button className="modal-close" onClick={onClose} aria-label="Fechar" type="button">
            <CloseIcon size={20} />
          </button>
          <h2>Entrar</h2>
          <p className="modal-subtitle">Informe seu CPF ou CNPJ e senha para acessar precos, pedidos e carrinho.</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-field">
            <label htmlFor="cnpj">CPF ou CNPJ</label>
            <div className="modal-input-wrap">
              <input
                id="cnpj"
                type="text"
                inputMode="numeric"
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                value={cnpj}
                onChange={e => setCnpj(maskDocument(e.target.value))}
                required
                autoFocus
              />
            </div>
          </div>

          <div className="modal-field">
            <label htmlFor="password">Senha</label>
            <div className="modal-input-wrap">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Sua senha"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="modal-password-toggle"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
              </button>
            </div>
          </div>

          {error && <p className="modal-error" role="alert">{error}</p>}

          {showRegistrationGuidance && (
            <div className="modal-guidance" role="note" aria-label="Orientacao de cadastro">
              <strong>Cadastro ainda nao localizado</strong>
              <span>Procure a loja fisica e fale com o setor financeiro para liberar seu acesso com CPF ou CNPJ.</span>
            </div>
          )}

          <button type="submit" className="modal-submit" disabled={loading}>
            <LoginIcon size={18} />
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        {import.meta.env.VITE_DEMO_CNPJ && (
          <p className="modal-hint">
            Credenciais de demonstracao: CNPJ <strong>{import.meta.env.VITE_DEMO_CNPJ}</strong>
            {import.meta.env.VITE_DEMO_PASSWORD && <> , senha <strong>{import.meta.env.VITE_DEMO_PASSWORD}</strong></>}
          </p>
        )}
      </div>
    </div>
  );
}
